import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Modal } from '@/shared/ui/Modal'
import { Logo } from '@/shared/ui/Logo'
import {
  ExitIcon, LockIcon, MailIcon, MenuIcon, MoonIcon, ShieldIcon, SunIcon, UserIcon,
} from '@/shared/ui/icons'
import { useAuth, useCabinet, useStores, useUi } from '@/stores/RootStore'
import { AccountDialogs } from './AccountDialogs'
import { CabinetDialogs } from './CabinetDialogs'
import styles from './AppLayout.module.css'

/*
  Разделы кабинета.

  В макете их ровно два: «Обзор» и «История», у администратора между
  ними появляется «Аккаунты». Пункт «Платёжные ссылки»
  есть у обоих: без него выставленную из кабинета ссылку негде ни
  найти, ни переслать покупателю повторно. Кошельки, автовыводы и выводы — не пункты
  меню, а блоки внутри «Обзора»: так в макете, и так короче путь до
  денег. Списки выводов и ссылок отдельных экранов в макете не имеют
  и открываются из тех же блоков.
*/
const LINKS = [
  { to: '/app', label: 'Обзор', end: true },
  { to: '/app/invoices', label: 'Платёжные ссылки', end: false },
  { to: '/app/history', label: 'История', end: false },
] as const

const ADMIN_LINKS = [
  { to: '/app/admin', label: 'Обзор', end: true },
  { to: '/app/admin/accounts', label: 'Аккаунты', end: false },
  { to: '/app/invoices', label: 'Платёжные ссылки', end: false },
  { to: '/app/history', label: 'История', end: false },
] as const

/**
 * Оболочка кабинета: шапка, подвал и колонка контента.
 *
 * Шапка липкая: на длинных экранах вроде истории на тысячу строк
 * навигация не должна уезжать вверх вместе со списком.
 */
export const AppLayout = observer(function AppLayout() {
  const auth = useAuth()
  const ui = useUi()
  const cabinet = useCabinet()
  const { admin } = useStores()
  const location = useLocation()
  const [menu, setMenu] = useState(false)
  const links = auth.isAdmin ? ADMIN_LINKS : LINKS

  /*
    Один переключатель на два стора.

    Админские ручки принимают `currency_view` так же, как кабинетные,
    но сводки у них разные, и держать их в одном сторе нельзя: сумма
    комиссии сервиса и баланс мерчанта — разные деньги. Поэтому
    переключатель на админских страницах правит админский стор,
    а на остальных — кабинетный, и обе половины остаются согласованными.
  */
  const inAdmin = location.pathname.startsWith('/app/admin')
  const view = inAdmin ? admin.view : cabinet.view
  const setView = (next: 'usd' | 'rub') =>
    inAdmin ? admin.setView(next) : cabinet.setView(next)

  const fiat = (
    <div className={styles.fiat} role="group" aria-label="Валюта отображения">
      {(['usd', 'rub'] as const).map((code) => (
        <button key={code} type="button" aria-pressed={view === code}
                className={view === code ? `${styles.fiatItem} ${styles.on}` : styles.fiatItem}
                onClick={() => setView(code)}>
          <span className={styles.fiatLabel}>{code.toUpperCase()}</span>
        </button>
      ))}
    </div>
  )

  return (
    <div className={styles.shell}>
      <header className={styles.bar}>
        {/* Логотип ведёт в СВОЙ обзор: у администратора это /app/admin.
            С общей ссылкой на /app он попадал на кабинет мерчанта —
            свой, пустой, с чужими на вид цифрами — и решал, что
            админка отвалилась. */}
        <NavLink className={() => styles.logo} to={auth.isAdmin ? '/app/admin' : '/app'}
                 aria-label="CryptoChangeK">
          <Logo className={styles.logoWide} />
        </NavLink>

        <nav className={styles.nav}>
          {links.map((link) => (
            <NavLink key={link.label} to={link.to} end={link.end}
                     className={({ isActive }) =>
                       isActive ? `${styles.link} ${styles.here}` : styles.link}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.right}>
          <Button variant="icon" onClick={() => ui.toggleTheme()}
                  aria-label={ui.theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
            {ui.theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </Button>

          <div className={styles.fiatWide}>{fiat}</div>

          <ProfileMenu />

          <button type="button" className={styles.burger}
                  aria-label="Разделы" aria-expanded={menu}
                  onClick={() => setMenu((v) => !v)}>
            <MenuIcon />
          </button>
        </div>
      </header>

      {/* На телефоне разделы и валюта отображения прячутся под «бургер»:
          в строку шириной 375px они не помещаются никаким кеглем. */}
      {menu ? (
        <nav className={styles.sheet} onClick={() => setMenu(false)}>
          {links.map((link) => (
            <NavLink key={link.label} to={link.to} end={link.end}
                     className={({ isActive }) =>
                       isActive ? `${styles.sheetLink} ${styles.here}` : styles.sheetLink}>
              {link.label}
            </NavLink>
          ))}
          <div className={styles.sheetFiat}>{fiat}</div>
        </nav>
      ) : null}

      {/* key по пути: экран перемонтируется на переход и «встаёт на место»
          (анимация .view). По изменению query-параметров — нет. */}
      <main className={styles.body}>
        <div key={location.pathname} className={styles.view}><Outlet /></div>
      </main>

      {/* Диалоги аккаунта живут здесь, а не в меню профиля: попап
          закрывается по клику мимо, и диалог, смонтированный внутри
          него, исчезал бы вместе с ним — вместе с введённым паролем. */}
      <AccountDialogs />
      <CabinetDialogs />

      <footer className={styles.foot}>
        {`© CryptoChangeK, ${new Date().getFullYear()}`}
      </footer>
    </div>
  )
})

const ProfileMenu = observer(function ProfileMenu() {
  const auth = useAuth()
  const cabinet = useCabinet()
  const { settings } = useStores()
  const [open, setOpen] = useState(false)
  // Выход спрашивает подтверждение: пункт «Выйти» стоит в меню сразу
  // под «Подключить 2FA», и промах мышью выбрасывал из кабинета
  // посреди заполнения формы вывода — со всем, что было введено.
  const [asking, setAsking] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Каждый пункт открывает свой диалог: отдельной страницы настроек
  // в макете нет, и меню профиля — единственный вход в них.
  const open2fa = () => {
    setOpen(false)
    if (auth.user?.['2fa_enabled']) settings.askDisable()
    else void settings.startTwoFactor()
  }

  return (
    <div className={styles.profileWrap} ref={boxRef}>
      <Button variant="icon" onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu" aria-expanded={open} aria-label="Профиль">
        <UserIcon size={24} />
      </Button>

      {open ? (
        <div className={styles.menu} role="menu">
          {/* Почта — тоже пункт меню, а не подпись: в макете все четыре
              строки одинаковые select-item, и по первой меняют адрес. */}
          <button type="button" className={styles.menuItem} role="menuitem"
                  onClick={() => { setOpen(false); settings.openEmail() }}>
            <MailIcon /><span className={styles.menuLabel}>{auth.user?.email}</span>
          </button>
          <button type="button" className={styles.menuItem} role="menuitem"
                  onClick={() => { setOpen(false); settings.openPassword() }}>
            <LockIcon /><span className={styles.menuLabel}>Изменить пароль</span>
          </button>
          <button type="button" className={styles.menuItem} role="menuitem"
                  onClick={open2fa}>
            <ShieldIcon size={20} />
            <span className={styles.menuLabel}>
              {auth.user?.['2fa_enabled'] ? 'Управление 2FA' : 'Подключить 2FA'}
            </span>
          </button>
          <button type="button" className={styles.menuItem} role="menuitem"
                  onClick={() => { setOpen(false); setAsking(true) }}>
            <ExitIcon size={20} /><span className={styles.menuLabel}>Выйти</span>
          </button>
        </div>
      ) : null}

      {/* Вопрос — подзаголовок диалога (note), а не тело. В макете
          (Exit 4:19694) он стоит под заголовком с зазором 8, и карточка
          212px высотой; отдельным телом он добавлял 32px зазора. */}
      {asking ? (
        <Modal title="Выйти?" note="Вы уверены, что хотите выйти из аккаунта?"
               onClose={() => setAsking(false)}
               actions={
                 <>
                   <Button variant="secondary" onClick={() => setAsking(false)}>Не выходить</Button>
                   <Button variant="primary"
                           onClick={() => { setAsking(false); cabinet.reset(); auth.logout() }}>
                     Да, выйти
                   </Button>
                 </>
               } />
      ) : null}
    </div>
  )
})
